import * as React from 'react'
import {faPlusCircle} from '@fortawesome/free-solid-svg-icons'
import {H1} from '@frontrow/console/src/components/typography'
import {Button} from '@frontrow/console/src/components/button'
import {SpinnerOverlay} from '@frontrow/console/src/components/spinner'

import ErrorAlert from '@frontrow/console/src/components/common/error-alert'
import PanelList from '@frontrow/console/src/components/common/panel-list'
import {LICENSE_GRID} from '@frontrow/console/src/components/pages/teachers-page/teacher-licenses/license-row/license-row'
import {
  type TeacherLicenseAllocationT,
  type TeacherLicenseAllocationPostT
} from '@frontrow/console/src/models/teacher-license-allocation'
import NewLicense from '@frontrow/console/src/components/pages/teachers-page/teacher-licenses/new-license'

type Props = {
  teacherId: number
  licenses: Array<TeacherLicenseAllocationT>
  error: string | undefined | null
  isLoading: boolean
  addingLicense: boolean
  renderListElement: (license: TeacherLicenseAllocationT, index: number) => React.ReactElement
  onAddLicense: () => void
  onCancelAddLicense: () => void
  onCreateLicense: (newLicense: TeacherLicenseAllocationPostT) => void
}

const HEADING_CELL = 'text-xs font-semibold uppercase tracking-wide text-muted-text'

export default function TeacherLicenses(props: Props): React.ReactElement {
  const {
    teacherId,
    licenses,
    error,
    isLoading,
    addingLicense,
    renderListElement,
    onAddLicense,
    onCancelAddLicense,
    onCreateLicense
  } = props

  const errorNode = error !== null && error !== undefined ? <ErrorAlert>{error}</ErrorAlert> : null

  // Nothing to head when there are no rows: the empty state stands alone.
  const columnHeadings =
    licenses.length > 0 ? (
      <div className={LICENSE_GRID}>
        <div className={HEADING_CELL}>Subject</div>
        <div className={HEADING_CELL}>Starts</div>
        <div className={HEADING_CELL}>Expires</div>
        <div className={HEADING_CELL}>Status</div>
        <div />
      </div>
    ) : null

  const body = isLoading ? (
    <SpinnerOverlay />
  ) : (
    <PanelList renderListElement={renderListElement} dataList={licenses} header={columnHeadings} />
  )

  const addLicenseToggleNode = addingLicense ? (
    <NewLicense createTeacherLicense={onCreateLicense} onCancel={onCancelAddLicense} />
  ) : (
    <div className="my-4">
      <Button
        style="primary"
        leftIcon={faPlusCircle}
        onClick={onAddLicense}
        dataTest="add-new-license-button"
      >
        Add license
      </Button>
    </div>
  )

  return (
    <React.Fragment>
      <H1>License allocations for teacher id {teacherId}</H1>
      {errorNode}
      {addLicenseToggleNode}
      <div>{body}</div>
    </React.Fragment>
  )
}
